import React, { useState, Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, ButtonSave } from "components/buttons";
import { Dialog } from "@blueprintjs/core";
import { WizardSteps } from "components/wizard";
import { ShowHide } from "components/layout";
import { AlertUnsavedData } from "components/notifications/AlertUnsavedData";
import { ModalSize } from "./ModalSize";
import classNames from "classnames";
import { NotificationInline } from 'components/notifications';
import './Notifications.css';

export function WizardModal({
    children,
    isOpen,
    title,
    header,
    id = "",
    allowBack = true,
    canProceed,
    onStepChange,
    onFinish,
    disabled = false,
    loading = false,
    canFinish = false,
    finishText = "Save and close",
    finishIcon = "floppy-disk",
    onCancel,
    isDirty,
    size = ModalSize.LARGE,
    resetOnCancel = false,

    additionalCompleteButton = {
        text: "",
        onClick: () => null,
        canProceed: false,
        canCloseModalOnSuccess: false
    },

    additionalButtons,
    notificationMessage,
    notificationIntent
}) {
    const [currentState, setCurrentState] = useState({
        step: 0,
        direction: "forward",
        previousStep: 0,
        lastStepIndex: 0,
        canFinish
    });

    const [totalSteps, setTotalSteps] = useState(0);
    const [showDirtyModal, setShowDirtyModal] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!children || !Array.isArray(children)) {
            setTotalSteps(1);
            return;
        }
        setTotalSteps(children.filter(c => c !== null).length);
    }, [children]);

    function onStepChangeInternal() {
        if (totalSteps === 0) {
            return;
        }
        if (onStepChange) {
            onStepChange(
                currentState.step,
                currentState.direction,
                currentState.previousStep,
                currentState.lastStepIndex,
                currentState.canFinish
            );
        }
    }

    useEffect(onStepChangeInternal, [currentState.step]);

    function onNextStep() {
        moveToStep(currentState.step + 1);
    }

    function onPreviousStep() {
        moveToStep(currentState.step - 1);
    }

    function moveToStep(step) {
        setCurrentState((state) => {
            return {
                ...state,
                step: step,
                direction: step >= state.step ? "forward" : "backward",
                previousStep: step - 1,
                lastStepIndex: totalSteps - 1,
                canFinish: canFinish
            };
        });
    }

    function onCancelWizard() {
        if (!isDirty) {
            resetOnCancel ? resetAndClose() : onCancel();
        } else {
            //dirty changes, so show modal to confirm exit
            setShowDirtyModal(true);
        }
    }

    function resetAndClose() {
        setCurrentState({
            step: 0,
            direction: "forward",
            previousStep: 0,
            lastStepIndex: 0,
            canFinish: false
        });
        setShowDirtyModal(false);
        onCancel();
    }

    function onFinishWizard() {
        setSaving(true);
        onFinish()
            .then(() => {
                resetAndClose();
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onAdditionalButton() {
        if (additionalCompleteButton.onClick) {
            setSaving(true);
            additionalCompleteButton.onClick()
                .then(() => {
                    if (additionalCompleteButton.canCloseModalOnSuccess) {
                        resetAndClose();
                    }
                })
                .finally(() => {
                    setSaving(false);
                });
        }
    }

    function mapSteps() {
        //Allow for single child elsement
        if (children) {
            if (!Array.isArray(children)) {
                return children;
            };
            return children.filter(c => c !== null).map((child, i) => {
                if (i === currentState.step) {
                    return child;
                }
                return null;
            })
        } else {
            return null;
        }
    }

    return (
        <Fragment>
            <Dialog
                isOpen={isOpen}
                title={title}
                isCloseButtonShown={false}
                className={classNames("modal-holder modal-holder-wizard", size)}
            >
                <ShowHide
                    evaluator={totalSteps > 1}
                    show={(
                        <div className="spacer-bottom">
                            <WizardSteps
                                currentStep={currentState.step}
                                totalSteps={totalSteps}
                                onStepClick={() => { }}
                                loading={loading}
                                wizardComplete={canFinish}
                                minimal
                            />
                        </div>
                    )}
                />
                <div>
                    {header}
                </div>
                <div className="bp6-dialog-body modal-holder-wizard-steps" id={id}>
                    {mapSteps()}
                </div>
                <NotificationInline allowClose={false} text={notificationMessage} intent={notificationIntent} show={notificationMessage && notificationMessage.length > 0} isBanner={true} additionalStyle={{ marginTop: '10px' }} />
                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            intent="secondary"
                            loading={loading}
                            text={isDirty ? "Cancel" : "Close"}
                            onClick={onCancelWizard}
                            disabled={disabled || saving}
                            id="cancel-btn"
                        />
                        <ShowHide
                            evaluator={allowBack && currentState.step > 0}
                            show={
                                <Button
                                    intent="primary"
                                    loading={loading}
                                    text="Back"
                                    onClick={onPreviousStep}
                                    disabled={disabled || saving}
                                    id="prev-btn"
                                />
                            }
                        />
                        {additionalButtons}
                        <ShowHide
                            evaluator={currentState.step < totalSteps - 1}
                            show={
                                <Button
                                    intent="primary"
                                    loading={loading}
                                    text="Next"
                                    onClick={onNextStep}
                                    disabled={disabled || !canProceed || saving}
                                    id="next-btn"
                                />
                            }
                        />
                        <ShowHide
                            evaluator={currentState.step >= totalSteps - 1 && canFinish}
                            show={
                                <ButtonSave
                                    intent="primary"
                                    loading={loading}
                                    text={finishText}
                                    icon={finishIcon}
                                    onClick={onFinishWizard}
                                    disabled={saving}
                                    simpleDisabled={!canProceed || disabled}
                                    id="finish-btn"
                                />
                            }
                        />

                        <ShowHide
                            evaluator={
                                additionalCompleteButton != null &&
                                additionalCompleteButton.show &&
                                additionalCompleteButton.text && 
                                additionalCompleteButton.text.length > 0
                            }
                            show={
                                <ButtonSave
                                    intent="primary"
                                    loading={loading}
                                    text={additionalCompleteButton.text}
                                    onClick={onAdditionalButton}
                                    disabled={saving}
                                    simpleDisabled={!additionalCompleteButton.canProceed || disabled}
                                    id="additional-btn"
                                />
                            }
                        />
                    </div>
                </div>
            </Dialog>
            <AlertUnsavedData
                isDirty={isOpen && isDirty}
                isDirtySamePage={isOpen && isDirty && showDirtyModal}
                onConfirmLeave={resetAndClose}
                onCancelLeave={() => setShowDirtyModal(false)}
            />
        </Fragment>
    );
}

WizardModal.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    isOpen: PropTypes.bool.isRequired,
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
    ]).isRequired,
    header: PropTypes.node,
    id: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    allowBack: PropTypes.bool,
    canProceed: PropTypes.bool.isRequired,
    onStepChange: PropTypes.func,
    canFinish: PropTypes.bool,
    finishText: PropTypes.string,
    finishIcon: PropTypes.string,
    onFinish: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isDirty: PropTypes.bool.isRequired,
    size: PropTypes.oneOf(["small", "medium", "large", "superwide"]),
    resetOnCancel: PropTypes.bool,
    additionalCompleteButton: PropTypes.exact({
        text: PropTypes.string,
        onClick: PropTypes.func,
        canProceed: PropTypes.bool,
        canCloseModalOnSuccess: PropTypes.bool,
        show: PropTypes.bool
    }),
    additionalButtons: PropTypes.node,
    notificationMessage: PropTypes.string,
    notificationIntent: PropTypes.string
};
